import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";
import todayStore from "./today";
import nextupStore from "./nextup";
import { calculateWithMRAModel, calculateTotalPaidAmount, calculatePossibleWinnings, calculateTaxWithMRAModel } from "@/utils/wagerTaxCalculator";
import { calculateNoTax,  calculatePossibleWinningsNoTax,  calculateTotalPaidAmountNoTax } from "@/utils/wagerNoTaxCalculator";
import { getSelectionId } from "@/utils/betslip";
import { compareDateTime } from "@/utils/utils";
import { parseEachWayFraction } from "@/utils/eachWayFraction";

Vue.use(Vuex);

const SPOdds = 'SP'
const MARKET_TYPE = {
  EACH_WAY: 'win_or_ew',
  PLACE: 'place',
  WIN: 'win',
  SP: 'sp',
  TRICAST: 'tricast',
  FORECAST: 'forecast',
}

const getStoragePrefix = () => {
  try {
    const urlObj = new URL(window.location.href);
    const params = urlObj.searchParams;
    const account = params.get('account') || 'guest';
    const token = params.get("token");
    let userId = 'guest';
    let isLogin = false;

    if (token) {
      const decodedToken = parseJwt(token);

      if (decodedToken) {
        const currentTime = Date.now() / 1000;

        // Check if token is expired
        if (decodedToken.exp && decodedToken.exp < currentTime) {
          isLogin = false;
        } else if (decodedToken.user_id) {
          userId = decodedToken.user_id;
          isLogin = true;

          // Migrate all state from guest to user if needed
          const guestPrefix = `${account}_guest_`;
          const userPrefix = `${account}_${userId}_`;
          const guestState = localStorage.getItem(guestPrefix + 'vuex');

          if (guestState) {
            try {
              const parsedGuestState = JSON.parse(guestState);
              // Update login state in the migrated state
              parsedGuestState.isLogin = true;
              // Save entire state to user storage
              localStorage.setItem(userPrefix + 'vuex', JSON.stringify(parsedGuestState));
              // Clear guest storage
              localStorage.removeItem(guestPrefix + 'vuex');
            } catch (error) {
              console.error("Error migrating state:", error);
            }
          }
        } else {
          console.warn("Token does not contain user_id");
        }
      }
    }

    return {
      prefix: `${account}_${userId}_`,
      isLogin
    };
  } catch (error) {
    console.error("Error in getStoragePrefix:", error);
    return {
      prefix: `${Math.random().toString(36).substring(7)}_`,
      isLogin: false
    };
  }
};

function parseJwt(token) {
  try {
    // Check if token has correct JWT format (header.payload.signature)
    if (!token || typeof token !== 'string' || !token.includes('.')) {
      return null;
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const base64Url = parts[1];
    // Add padding if needed
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - base64Url.length % 4) % 4);

    try {
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      return JSON.parse(jsonPayload);
    } catch (decodeError) {
      return null;
    }
  } catch (error) {
    return null;
  }
}

const isEligibleForEachWayMultiple = (betslips, gameType = 'hr') => {
  const current = betslips.filter((x) => x.gameType === gameType);
  return current.length > 0 && current.every((x) => x.isEW && x.runners >= 5);
};

const isShowEachWayMultiple = (state) => {
  const gameType = state.settings.currentGame ?? 'hr';
  return state.settings.currentTab === 'MULTIPLE'
    && isEligibleForEachWayMultiple(state.betslips, gameType);
};

const isEachWayMultipleActive = (state) => {
  const gameType = state.settings.currentGame ?? 'hr';
  return Boolean(state.eachWayBetMultiple && isEligibleForEachWayMultiple(state.betslips, gameType));
};

/** Clear stale EW-multiple flag when slip is no longer all win_or_ew (e.g. forecast added). */
const clearInvalidEachWayMultiple = (state) => {
  if (!state.eachWayBetMultiple) return;
  const gameType = state.settings.currentGame ?? 'hr';
  if (isEligibleForEachWayMultiple(state.betslips, gameType)) return;

  state.eachWayBetMultiple = false;
  state.betslips = state.betslips.map((bet) =>
    bet.gameType === gameType ? { ...bet, eachWayBet: false } : bet
  );
};

// Helper function to calculate total win
const calculateTotalWin = (betslips, currentTab, totalStakeMultiple, eachWayBetMultiple = false) => {
  // case SP or NA
  if (betslips.some((x) => x.odds == null || x.odds === "SP")) {
    return 0;
  }

  let totalWin = 0;

  // case single 
  if (currentTab === 'SINGLE') {
    totalWin = betslips.reduce((total, bet) => {
      if (!bet.odds) return total;
      const winAmount = bet.eachWayBet
        ? ((bet.odds * bet.amount) + (((bet.odds - 1) * parseEachWayFraction(bet.eachWayFraction) + 1) * bet.amount))
        : bet.amount * bet.odds;
      return total + winAmount;
    }, 0);
  }

  // case multiple
  if (currentTab === 'MULTIPLE') {
    // check case all each way
    if (eachWayBetMultiple && betslips.every((x) => x.isEW && x.runners >= 5)) {
      let totalWinTemp = 0;
      let amount = +totalStakeMultiple;
      betslips.forEach(bet => {
        totalWinTemp = ((bet.odds * amount) + (((bet.odds - 1) * parseEachWayFraction(bet.eachWayFraction) + 1) * amount));
        amount = totalWinTemp / 2;
      });
      totalWin = totalWinTemp;
    } else {
      const multipOdds = betslips.reduce((total, bet) => {
        if (!bet.odds || bet.odds === "SP") return total;
        return total * +bet.odds;
      }, 1);
      totalWin = +(multipOdds * +totalStakeMultiple);
    }
  }

  return totalWin;
};

const getOddsByMarketType = (marketType, item) => {
  if (marketType == MARKET_TYPE.EACH_WAY || marketType == MARKET_TYPE.WIN) return item.win_odd;
  if (marketType == MARKET_TYPE.PLACE) return item.place_odd;
  return SPOdds;
}

const genderMarketTypeName = (marketType) => {
  if (marketType == MARKET_TYPE.EACH_WAY || (marketType == SPOdds.toLowerCase())) return "Win or E/W";
  if (marketType == marketType.WIN || marketType == marketType.PLACE || marketType == SPOdds) return "fixed " + marketType;
  return marketType;
}

const betslipsForCurrentGame = (state) => {
  const gameType = state.settings?.currentGame ?? "hr";
  return state.betslips.filter((bet) => bet.gameType === gameType);
};

// Initialize storage prefix
const { prefix: STORAGE_PREFIX, isLogin: initialLoginState } = getStoragePrefix();

// Create store instance
const store = new Vuex.Store({
  modules: {
    nextupStore,
    todayStore,
  },
  state: {
    //invalidBets: [] as any,
    isLogin: initialLoginState,
    settings: {} as any,
    betslips: [] as any[],
    race: {},
    totalStakeMultiple: 0,
    eachWayBetMultiple: false,
  },
  mutations: {
    ADD_BETSLIP(state, betslip) {
      const wagerTax = state.settings.wagerTax || 0;
      const wagerTaxType = state.settings.wagerTaxType;
      const taxWinnings = state.settings.taxWinnings || 0;

      if (wagerTax > 0) {
        const taxResult = calculateWithMRAModel(betslip, wagerTax, wagerTaxType);
        betslip.totalPaidAmount = taxResult.totalPaidAmount;
        betslip.tax = taxResult.taxOnWager;
        betslip.wagerAfterTax = taxResult.wagerAfterTax;
        betslip.possibleWinnings = taxResult.possibleWinnings;
      } else {
        const noTaxResult = calculateNoTax(betslip, taxWinnings);
        betslip.odds = noTaxResult.odds;
        betslip.winningTax = noTaxResult.winningTax;
        betslip.possibleWinnings = noTaxResult.possibleWinnings;
      }
      state.betslips.push(betslip);
      clearInvalidEachWayMultiple(state);
    },

    SET_BETSLIP(state, payload) {
      state.betslips = payload;
      clearInvalidEachWayMultiple(state);
    },

    REMOVE_BETSLIP(state, bet) {
      // get betslip allow game type
      const gameType = state.settings.currentGame ?? "hr";
      const betslip = state.betslips.find(x => x.gameType == gameType && x.id == bet.id);

      if (betslip) {
        state.betslips = state.betslips.filter(x => x.id != betslip.id);
        clearInvalidEachWayMultiple(state);
      }
    },

    REMOVE_ALL_BETSLIP(state) {
      const gameType = state.settings.currentGame ?? "hr";
      state.betslips = state.betslips.filter(x => x.gameType != gameType);
      clearInvalidEachWayMultiple(state);
    },

    CHANGE_EACH_WAY_BET(state, { bet, eachWayBet }) {
      const gameType = state.settings.currentGame ?? "hr";
      const betslip = state.betslips.find((x: any) => x.gameType == gameType && x.id == bet.id);
      if (betslip) {
        betslip.eachWayBet = eachWayBet;

        // case: wager tax
        const wagerTax = state.settings.wagerTax || 0;
        const wagerTaxType = state.settings.wagerTaxType;

        if (wagerTax > 0) {
          const taxResult = calculateWithMRAModel(betslip, wagerTax, wagerTaxType);
          betslip.totalPaidAmount = taxResult.totalPaidAmount;
          betslip.tax = taxResult.taxOnWager;
          betslip.wagerAfterTax = taxResult.wagerAfterTax;
          betslip.possibleWinnings = taxResult.possibleWinnings;
        }
        else {
          const noTaxResult = calculateNoTax(betslip, state.settings.taxWinnings);
          betslip.winningTax = noTaxResult.winningTax;
          betslip.possibleWinnings = noTaxResult.possibleWinnings;
        }
      }
    },

    UPDATE_BETSLIP(state, { bet, amount }) {
      const wagerTax = state.settings.wagerTax || 0;
      const wagerTaxType = state.settings.wagerTaxType;

      const gameType = state.settings.currentGame ?? "hr";
      const betslip = state.betslips.find((x: any) => x.gameType == gameType && x.id == bet.id);
      if (betslip) {
        betslip.amount = amount;

        // case: wager tax
        if (wagerTax > 0) {
          const taxResult = calculateWithMRAModel(betslip, wagerTax, wagerTaxType);
          betslip.totalPaidAmount = taxResult.totalPaidAmount;
          betslip.tax = taxResult.taxOnWager;
          betslip.wagerAfterTax = taxResult.wagerAfterTax;
          betslip.possibleWinnings = taxResult.possibleWinnings;
        } else {
          const noTaxResult = calculateNoTax(betslip, state.settings.taxWinnings);
          betslip.winningTax = noTaxResult.winningTax;
          betslip.possibleWinnings = noTaxResult.possibleWinnings;
        }
      }
    },

    UPDATE_TOTAL_STAKE_MULTIPLE(state, payload) {
      state.totalStakeMultiple = payload;
    },

    UPDATE_EACH_WAY_BET_MULTIPLE(state, payload) {
      const gameType = state.settings.currentGame ?? "hr";
      const enabled = Boolean(payload) && isEligibleForEachWayMultiple(state.betslips, gameType);
      state.eachWayBetMultiple = enabled;
      // Chỉ update betslips của game hiện tại
      state.betslips = state.betslips.map(bet =>
        bet.gameType === gameType
          ? { ...bet, eachWayBet: enabled }
          : bet
      );
    },

    UPDATE_BETSLIP_BEFORE_ADD(state, { item, marketType }) {
      const gameType = state.settings.currentGame ?? "hr";
      const selectionId = getSelectionId(item);
      const betslip = state.betslips.find((x) => {
        if (x.gameType !== gameType) return false;
        if (selectionId != null) {
          return x.selectionId === selectionId && x.marketType === marketType;
        }
        return x.fieldId === item.field_id;
      });
      if (betslip) {
        if (betslip.marketType == marketType) {
          state.betslips = state.betslips.filter(x => x.id != betslip.id);
        } else {
          // update betslip by item
          const wagerTax = state.settings.wagerTax || 0;
          const wagerTaxType = state.settings.wagerTaxType;
          // get odds by market type
          const odds = getOddsByMarketType(marketType, item);

          betslip.odds = odds;
          betslip.marketType = marketType;
          betslip.marketTypeName = genderMarketTypeName(marketType);
          betslip.isEW = marketType === MARKET_TYPE.EACH_WAY;

          if (wagerTax > 0) {
            const taxResult = calculateWithMRAModel(betslip, wagerTax, wagerTaxType);
            betslip.totalPaidAmount = taxResult.totalPaidAmount;
            betslip.tax = taxResult.taxOnWager;
            betslip.wagerAfterTax = taxResult.wagerAfterTax;
            betslip.possibleWinnings = taxResult.possibleWinnings;
          } else {
            const noTaxResult = calculateNoTax(betslip, state.settings.taxWinnings);
            betslip.winningTax = noTaxResult.winningTax;
            betslip.possibleWinnings = noTaxResult.possibleWinnings;
          }
        }
        clearInvalidEachWayMultiple(state);
        return true;
      }

      return false;
    },

    SET_RACE(state, payload) {
      if (payload?.race_time && state.settings?.timeZone) {
        if (compareDateTime(payload.race_time, Date.now(), state.settings.timeZone) <= 0) {
          Vue.set(payload, "isStarted", true);
        }
      }
      state.race = payload;
    },

    // setInValidBets(state, payload: any) {
    //   state.invalidBets = payload;
    // },

    // removeInvalidBets(state, payload: any) {
    //   const index = state.invalidBets.findIndex((x) => x == payload);
    //   if (index > -1) {
    //     state.invalidBets.splice(index, 1);
    //   }
    // },

    setLogin(state, payload) {
      state.isLogin = payload;
    },

    updateSettings(state, payload) {
      state.settings = payload;
    },

    updateSettingByName(state, payload) {
      state.settings[payload.name] = payload.value;
    },

    UPDATE_BETSLIP_BY_ID(state, updatedBetslip) {
      const index = state.betslips.findIndex(b => b.id === updatedBetslip.id);
      if (index !== -1) {
        Vue.set(state.betslips, index, { ...state.betslips[index], ...updatedBetslip });
      }
    },
  },
  actions: {
    updateBetslips({ commit }, payload) {
      commit("SET_BETSLIP", payload);
    },

    // addInvalidBet({ commit }, payload) {
    //   commit("setInValidBets", payload);
    // },

    // removeInvalidBet({ commit }, payload) {
    //   commit("removeInvalidBets", payload);
    // },

    setLogin({ commit }, payload) {
      commit("setLogin", payload);
    },

    updateSettings({ commit }, payload) {
      commit("updateSettings", payload);
    },

    updateSettingByName({ commit }, payload) {
      commit("updateSettingByName", payload);
    },

    handleAddBetslip({ commit }, payload) {
      commit("ADD_BETSLIP", payload);
    },

    updateEachWayBetMultiple({ commit }, payload) {
      commit("UPDATE_EACH_WAY_BET_MULTIPLE", payload);
    },

    handleChangeStakeMultiple({ commit }, payload) {
      commit("UPDATE_TOTAL_STAKE_MULTIPLE", payload);
    },

    handleUpdateBetslipBeforeAdd({ commit, state }, payload) {
      const gameType = state.settings.currentGame ?? "hr";
      const selectionId = getSelectionId(payload.item);
      const betslip = state.betslips.find((x) => {
        if (x.gameType !== gameType) return false;
        if (selectionId != null) {
          return x.selectionId === selectionId && x.marketType === payload.marketType;
        }
        return x.fieldId === payload.item.field_id;
      });
      if(betslip) {
        commit("UPDATE_BETSLIP_BEFORE_ADD", payload);
        return true;
      }
      return false;
    },

    updateBetslipFromSocket({ commit, state }, { currentBetslip, invalidBetslip, data }) {
      if (!data.field || currentBetslip.length === 0) return;

      const newInvalidBetslip = invalidBetslip ? [...invalidBetslip] : [];
      currentBetslip.forEach((item) => {
        if (item.raceExternalId == data.external_id) {
          const newField = data.field.find((x) => x.number === item.number);
          if (newField) {
            let isChanged = false;
            const updatedItem = { ...item };

            // Check and update odds
            const newOdds = item.odds === "SP"
              ? "SP"
              : (newField.price == 0 || newField.price == null ? item.odds : newField.price);
            if (item.odds !== newOdds) {
              updatedItem.isIncrease = item.odds !== "SP" && newOdds > item.odds;
              updatedItem.odds = newOdds;
              isChanged = true;

              // Tính lại possibleWinnings và tax nếu cần
              const wagerTax = state.settings.wagerTax || 0;
              const wagerTaxType = state.settings.wagerTaxType;
              if (wagerTax > 0) {
                const taxResult = calculateWithMRAModel(updatedItem, wagerTax, wagerTaxType);
                updatedItem.totalPaidAmount = taxResult.totalPaidAmount;
                updatedItem.tax = taxResult.taxOnWager;
                updatedItem.wagerAfterTax = taxResult.wagerAfterTax;
                updatedItem.possibleWinnings = taxResult.possibleWinnings;
              } else {
                const noTaxResult = calculateNoTax(updatedItem, state.settings.taxWinnings);
                updatedItem.winningTax = noTaxResult.winningTax;
                updatedItem.possibleWinnings = noTaxResult.possibleWinnings;
              }
            }

            // Update eachWayFraction, eachWayPlaces, runners if changed
            ["eachWayFraction", "eachWayPlaces", "runners"].forEach(key => {
              const newValue = data[key.toLowerCase()] || item[key];
              if (item[key] !== newValue) {
                updatedItem[key] = newValue;
                isChanged = true;
              }
            });

            // Handle scratched status
            if (newField.status === "scratched") {
              if (!newInvalidBetslip.includes(item.fieldId)) {
                newInvalidBetslip.push(item.fieldId);
                isChanged = true;
              }
            } else {
              const index = newInvalidBetslip.indexOf(item.fieldId);
              if (index > -1) {
                newInvalidBetslip.splice(index, 1);
                isChanged = true;
              }
            }

            // Chỉ commit nếu có thay đổi
            if (isChanged) {
              commit("UPDATE_BETSLIP_BY_ID", updatedItem);
            }
          }
        }
      });
    },
  },

  getters: {
    // invalidBets: (state) => state.invalidBets,
    getBetslips: state => betslipsForCurrentGame(state),
    // total stake single
    totalStake: state => {
      const betslips = betslipsForCurrentGame(state);
      const wagerTax = state.settings.wagerTax || 0;
      const eachWayActive = isEachWayMultipleActive(state);
      if(wagerTax > 0) {
        return calculateTotalPaidAmount(state.settings.currentTab, betslips, state.settings.wagerTaxType, state.settings.wagerTax, state.totalStakeMultiple, state.eachWayBetMultiple);
      } else {
        return calculateTotalPaidAmountNoTax(state.settings.currentTab, betslips, state.totalStakeMultiple, state.eachWayBetMultiple);
      }
    },

    totalTax: state => {
      const betslips = betslipsForCurrentGame(state);
      // only have in tax 
      if (state.settings.currentTab === 'SINGLE') {
        return betslips.reduce((acc, betslip) => acc + Number(betslip.tax), 0)
      } else {
        // const wagerTax = state.settings.wagerTax || 0;
        // const taxOnWager = state.totalStakeMultiple * wagerTax;
        // return state.eachWayBetMultiple ? taxOnWager * 2 : taxOnWager;

        const { taxOnWager } = calculateTaxWithMRAModel(state.totalStakeMultiple, state.settings.wagerTax, isEachWayMultipleActive(state));
        return taxOnWager;
      }
    },

    totalWagerAfterTax: state => {
      const betslips = betslipsForCurrentGame(state);
      if (state.settings.currentTab === 'SINGLE') {
        return betslips.reduce((acc, betslip) => acc + Number(betslip.wagerAfterTax), 0)
      } else {
        // Noted: old model
        // if (state.settings.wagerTaxType === 'add') {
        //   return state.eachWayBetMultiple ? state.totalStakeMultiple * 2 : state.totalStakeMultiple;
        // } else {
        //   const wagerTax = state.settings.wagerTax || 0;
        //   let taxOnWager = state.totalStakeMultiple * wagerTax;
        //   taxOnWager = state.eachWayBetMultiple ? taxOnWager * 2 : taxOnWager;
        //   return state.eachWayBetMultiple ? state.totalStakeMultiple * 2 - taxOnWager : state.totalStakeMultiple - taxOnWager;
        // }
        const { wagerAfterTax } = calculateTaxWithMRAModel(state.totalStakeMultiple, state.settings.wagerTax, isEachWayMultipleActive(state));
        return wagerAfterTax;
      }
    },

    totalWinningTax: state => {
      const betslips = betslipsForCurrentGame(state);
      // case SP or NA
      if (betslips.some((x) => x.odds == null || x.odds === "SP")) {
        return SPOdds;
      }

      const totalWin = calculateTotalWin(betslips, state.settings.currentTab, state.totalStakeMultiple, state.eachWayBetMultiple);
      return totalWin * state.settings.taxWinnings;
    },

    totalPossibleWinnings: state => {
      const betslips = betslipsForCurrentGame(state);
      const wagerTax = state.settings.wagerTax || 0;
      const wagerTaxType = state.settings.wagerTaxType;
      const eachWayActive = isEachWayMultipleActive(state);

      if(wagerTax > 0) {
        return calculatePossibleWinnings(state.settings.currentTab, betslips, wagerTaxType, wagerTax, state.totalStakeMultiple, state.eachWayBetMultiple);
      } else {
        return calculatePossibleWinningsNoTax(state.settings.currentTab, betslips, state.totalStakeMultiple, state.eachWayBetMultiple);
      }
    },

    isSameRace: (state) => {
      // Chỉ check khi ở tab MULTIPLE
      if (state.settings.currentTab !== 'MULTIPLE') return false;
      const gameType = state.settings.currentGame ?? "hr";
      const betslips = state.betslips.filter(x => x.gameType === gameType);
      const raceIds = betslips.map(x => x.raceId).filter(Boolean);

      const raceCount: { [key: string]: number } = {};
      raceIds.forEach(raceId => {
        raceCount[raceId] = (raceCount[raceId] || 0) + 1;
      });

      const hasDuplicateRaces = Object.values(raceCount).some((count: number) => count > 1);
      return hasDuplicateRaces;
    },

    isInvalidMultipleOdds: (state) => {
      if (state.settings.currentTab !== 'MULTIPLE') return false;

      const betslips = betslipsForCurrentGame(state);
      if(betslips.some(x => x.odds == null || x.odds === 'SP')) {
        return false;
      }

      const minMultipleOdds = state.settings.multiple_bet_minimum_total_odds ?? 0;
      const totalOdds = betslips.reduce((total, bet) => {
        return total * +bet.odds;
      }, 1);
      return totalOdds < minMultipleOdds;
    },

    totalOdds: (state) => {
      const gameType = state.settings.currentGame ?? "hr";
      const betslips = state.betslips.filter(x => x.gameType === gameType);
      if (state.settings.currentTab === 'MULTIPLE') {
        if (betslips.some(x => x.odds == null || x.odds === 'SP')) {
          return 'SP';
        }
        const multipOdds = betslips.reduce((total, bet) => total * +bet.odds, 1);
        return Number(multipOdds.toFixed(2));
      }
      return 1;
    },
    isTax: (state) => {
      return state.settings.wagerTax > 0;
    },
    taxType: (state) => {
      return state.settings.wagerTaxType;
    }
  },

  plugins: [
    createPersistedState({
      key: STORAGE_PREFIX + 'vuex',
      paths: [
        'betslips',
        'settings',
        'totalStakeMultiple',
        'eachWayBetMultiple',
        'isLogin',
      ],
    })
  ],
});

export default store;
